import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Loader2, Smartphone } from "lucide-react";
import { BrandMark } from "@/components/BrandLogo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/use-language";
import { supabase } from "@/integrations/supabase/client";
import { QRCodeSVG } from "qrcode.react";

const socialLinks = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Youtube, href: "#", label: "YouTube" },
];

export function Footer() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSocials, setShowSocials] = useState(true);
  const [challenge, setChallenge] = useState(() => ({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  }));
  const [answer, setAnswer] = useState("");
  const showChallenge = email.trim().length > 0;
  const expected = useMemo(() => challenge.a + challenge.b, [challenge]);

  useEffect(() => {
    supabase
      .rpc("get_footer_social_enabled")
      .then(({ data, error }) => {
        if (!error && typeof data === "boolean") setShowSocials(data);
      });
  }, []);

  const footerLinks = {
    company: [
      { label: t.footer.about, href: "/about" },
      { label: t.footer.howItWorks, href: "/how-it-works" },
      { label: t.footer.forProfessionals, href: "/for-professionals" },
      { label: "Hire multiple providers", href: "/jobs/new" },
      { label: t.footer.pricing, href: "/pricing" },
      { label: "Advertise with us", href: "/advertise" },
      { label: t.footer.contactUs, href: "/contact" },
    ],
    support: [
      { label: t.footer.helpCenter, href: "/help" },
      { label: t.footer.contactUs, href: "/contact" },
      { label: t.footer.safetyGuidelines, href: "/safety" },
      { label: t.footer.trustVerification, href: "/trust" },
    ],
    legal: [
      { label: t.footer.termsConditions, href: "/terms" },
      { label: t.footer.privacyPolicy, href: "/privacy" },
      { label: t.footer.cookiePolicy, href: "/cookies" },
      { label: t.footer.refundPolicy, href: "/refund" },
    ],
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        title: t.common.error,
        description: t.footer.enterEmail,
        variant: "destructive",
      });
      return;
    }

    if (parseInt(answer, 10) !== expected) {
      toast({
        title: t.common.error,
        description: `Please answer the verification question: ${challenge.a} + ${challenge.b} = ?`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: email.trim(), source: 'footer' }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: t.common.success,
          description: t.footer.subscribeSuccess,
        });
        setEmail("");
        setAnswer("");
        setChallenge({ a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 });
      } else {
        toast({
          title: t.common.error,
          description: data?.error || t.errors.tryAgain,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: t.common.error,
        description: t.errors.tryAgain,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-foreground text-primary-foreground">
      {/* Main Footer */}
      <div className="container px-4 py-12 md:py-16">
        {/* Mobile: 2-column layout, Desktop: full layout */}
        <div className="grid grid-cols-2 gap-6 md:grid-cols-2 lg:grid-cols-6">
          {/* Brand & Newsletter - Full width on mobile */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="mb-4 inline-flex items-center">
              <BrandMark size="md" wordmarkClassName="text-2xl" />
            </Link>
            <p className="mb-6 text-sm font-medium text-primary-foreground/90 leading-relaxed">
              {t.footer.description}
            </p>

            {/* Newsletter with spam prevention */}
            <div className="mb-6">
              <h4 className="mb-2 font-semibold">{t.footer.stayUpdated}</h4>
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t.footer.enterEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
                  disabled={isLoading}
                />
                <Button type="submit" className="shrink-0" disabled={isLoading}>
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t.footer.subscribe}
                </Button>
              </form>
              {showChallenge && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm font-medium text-primary-foreground/90">
                    {challenge.a} + {challenge.b} =
                  </span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    aria-label={`Verification: what is ${challenge.a} plus ${challenge.b}?`}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    className="h-9 w-24 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground"
                    disabled={isLoading}
                  />
                  <span className="text-xs text-primary-foreground/70">Spam check</span>
                </div>
              )}
            </div>

            {/* Mobile Access QR Code - Hidden on mobile */}
            <div className="mt-6 pt-4 border-t border-primary-foreground/10 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="bg-white p-2 rounded-lg">
                  <QRCodeSVG
                    value="https://trade-hub-ng.lovable.app"
                    size={64}
                    level="M"
                    includeMargin={false}
                    role="img"
                    aria-label="QR code to open TaskRatel on your phone"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-primary-foreground">
                    <Smartphone className="h-4 w-4" />
                    <span>{t.footer.mobileAccess}</span>
                  </div>
                  <p className="text-xs text-primary-foreground/80 mt-0.5">
                    {t.footer.scanToOpen}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Company - desktop only; mobile links live in hamburger menu */}
          <div className="hidden md:block">
            <h4 className="mb-4 font-bold text-white">{t.footer.company}</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-medium text-primary-foreground/95 transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support - desktop only; mobile links live in hamburger menu */}
          <div className="hidden md:block">
            <h4 className="mb-4 font-bold text-white">{t.footer.support}</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-medium text-primary-foreground/95 transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal - desktop only; mobile links live in hamburger menu */}
          <div className="hidden md:block">
            <h4 className="mb-4 font-bold text-white">{t.footer.legal}</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-medium text-primary-foreground/95 transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Separator className="bg-primary-foreground/10" />

      {/* Bottom Bar */}
      <div className="container px-4 py-6 pb-24 md:pb-6">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          {/* Copyright */}
          <p className="text-center text-sm font-medium text-primary-foreground">
            © 2024 TaskRatel (A Glugum Company). {t.footer.copyright} {t.footer.builtWithLove}
          </p>

          {/* Social Links (admin-toggleable) */}
          {showSocials && (
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-primary-foreground transition-colors hover:text-primary"
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
